// views/Settings/SettingsSectionPage.js
// One page per Settings section: the band, the tabs, and the editor for the
// chosen tab rendered inside SettingsEmbedContext so existing screens drop
// their own page band. What the signed-in person may do comes from
// GET /api/platform-settings/access (the same gate the routes use), so an
// editor the person cannot change renders read-only with a note instead of
// disappearing.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import SettingsPanel, { SettingsEmbedContext } from "../../components/SettingsPanel";
import { THEME as C } from "../../components/dashboardTheme";
import { AppButton } from "../../components/ui";
import { apiPlatformSettingsAccess } from "../../api";
import { sectionByKey, sectionUrl } from "./settingsCatalogue";

const SettingsSectionPage = ({ sectionKey }) => {
  const section = sectionByKey(sectionKey);
  const history = useHistory();
  const location = useLocation();
  const [access, setAccess] = useState(null);
  const tabsRef = useRef(null);
  const activeTabRef = useRef(null);

  useEffect(() => {
    apiPlatformSettingsAccess()
      .then((res) => setAccess(res.data || {}))
      .catch(() => setAccess({ read: true, update: false, manage: false }));
  }, []);

  const tabs = section?.tabs || [];
  const requested = new URLSearchParams(location.search).get("tab");
  const active = useMemo(() => tabs.find((t) => t.key === requested) || tabs[0] || null, [tabs, requested]);
  useEffect(() => {
    const nav = tabsRef.current;
    const tab = activeTabRef.current;
    if (!nav || !tab) return undefined;
    const centerActive = () => {
      nav.scrollLeft = Math.max(0, tab.offsetLeft - nav.offsetLeft - (nav.clientWidth - tab.clientWidth) / 2);
    };
    centerActive();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(centerActive) : null;
    observer?.observe(nav);
    window.addEventListener("resize", centerActive);
    return () => { observer?.disconnect(); window.removeEventListener("resize", centerActive); };
  }, [active?.key]);

  const selectTab = (key) => history.replace(sectionUrl(section, key));

  if (!section) {
    return <SettingsPanel icon="fa-cog" title="Settings" subtitle="This section does not exist." />;
  }

  const canEdit = (tab) => {
    if (!access) return false;
    if (tab.level === "manage") return !!access.manage;
    return !!(access.update || access.manage);
  };

  const Editor = active?.component || null;

  return (
    <SettingsPanel icon={section.icon} title={section.label} subtitle={section.description} maxWidth={1080}>
      {/* Tabs: labels, not buttons, in the dashboard's filter style */}
      <nav ref={tabsRef} className="lk-settings-tabs" aria-label="Settings categories">
        {tabs.map((t) => {
          const on = active?.key === t.key;
          return (
            <button
              key={t.key}
              ref={on ? activeTabRef : undefined}
              type="button"
              aria-current={on ? "page" : undefined}
              onClick={() => selectTab(t.key)}
              style={{
                padding: "7px 14px", minHeight: 44, flexShrink: 0, whiteSpace: "nowrap", fontSize: 12, fontWeight: 600, borderRadius: 999, cursor: "pointer",
                border: `1px solid ${on ? C.header : C.border}`,
                background: on ? C.header : "#fff", color: on ? "#fff" : C.textMid,
              }}
            >
              {t.label}
              {t.level === "manage" && !on && (
                <span title="Changing this needs a super administrator" style={{ marginLeft: 6, color: C.textLight, fontWeight: 400 }}>SA</span>
              )}
            </button>
          );
        })}
      </nav>

      {active && active.href && (
        <SettingsPanel.Section title={active.label} icon="fa-arrow-up-right-from-square">
          <p style={{ margin: "0 0 14px", fontSize: 13, lineHeight: 1.6, color: C.textMid }}>{active.description}</p>
          <AppButton onClick={() => history.push(active.href)}>{active.hrefLabel || "Open"}</AppButton>
        </SettingsPanel.Section>
      )}

      {/* The editor waits for the access answer so a person who may edit never
          sees the read-only notice flash while it loads. */}
      {active && Editor && !access && (
        <div style={{ fontSize: 12, color: C.textLight, padding: "8px 0" }}>Checking your access...</div>
      )}
      {active && Editor && access && (
        <SettingsEmbedContext.Provider value={true}>
          <Editor canEdit={canEdit(active)} />
        </SettingsEmbedContext.Provider>
      )}
    </SettingsPanel>
  );
};

/** One component per section so routes.js can list them without closures. */
export const settingsPageFor = (sectionKey) => {
  const Page = () => <SettingsSectionPage sectionKey={sectionKey} />;
  Page.displayName = `SettingsSection(${sectionKey})`;
  return Page;
};

export default SettingsSectionPage;
